import { yupResolver } from '@hookform/resolvers/yup/dist/yup.js'
import { Alert, Box, Button, Chip, TextField, Typography } from '@mui/material'
import type { CardPartner as ContractCardPartner } from '@mynt/backoffice-api-spec/spendcontrols'
import { getAxiosErrorMessage } from 'api/axios/helpers'
import type { AxiosError } from 'axios'
import { CoreMyntModal } from 'components/modals/CoreMyntModal'
import { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'
import { useCategoryGroups, useCreateCategoryGroup } from './adapters'
import { isValidOrderInput } from './formUtils'
import type { SpendControlRow } from './SpendControlTable'

type FormValues = {
  name: string
  description: string
  order: string
}

const schema = yup.object({
  name: yup
    .string()
    .trim()
    .required()
    .test('not-dash', 'Name cannot be "-"', (v) => v?.trim() !== '-'),
  description: yup.string().default(''),
  order: yup
    .string()
    .default('')
    .test('valid-order', 'Must be a non-negative integer', (v) => isValidOrderInput(v ?? '')),
})

export const CreateCategoryGroupModal = ({
  open,
  onClose,
  partner,
}: {
  open: boolean
  onClose: () => void
  partner: string | undefined
}) => {
  const [categoryIds, setCategoryIds] = useState<string[]>([])
  const { mutate, isLoading, error, reset } = useCreateCategoryGroup()
  const { data: allRows = [] } = useCategoryGroups({ partner })

  const {
    register,
    handleSubmit,
    reset: resetForm,
    formState: { isValid, errors },
  } = useForm<FormValues>({
    defaultValues: { name: '', description: '', order: '' },
    mode: 'onChange',
    resolver: yupResolver(schema),
  })

  const allCategories = useMemo(
    () => [...new Map(allRows.filter((r) => r.categoryIds === undefined).map((r) => [r.id, r])).values()],
    [allRows],
  )
  const assignedCategories = useMemo(
    () => categoryIds.map((id) => allCategories.find((c) => c.id === id)).filter((c): c is SpendControlRow => c != null),
    [categoryIds, allCategories],
  )
  const availableCategories = useMemo(() => allCategories.filter((c) => !categoryIds.includes(c.id)), [allCategories, categoryIds])

  const handleClose = () => {
    resetForm()
    setCategoryIds([])
    reset()
    onClose()
  }

  const onSubmit = handleSubmit((data) => {
    const payload: Parameters<typeof mutate>[0] = {
      name: data.name.trim(),
      categoryIds,
      cardPartner: partner as ContractCardPartner,
    }
    if (data.description.trim().length > 0) {
      payload.description = data.description.trim()
    }
    const parsedOrder = data.order.trim() === '' ? undefined : Number(data.order.trim())
    if (parsedOrder !== undefined) {
      payload.order = parsedOrder
    }
    mutate(payload, { onSuccess: handleClose })
  })

  return (
    <CoreMyntModal
      wrapperProps={{ style: { minWidth: 550 } }}
      title="Create category group"
      onClose={handleClose}
      open={open}
      actions={() => (
        <Button variant="contained" disabled={!isValid || !partner || isLoading} onClick={onSubmit}>
          {isLoading ? 'Creating...' : 'Create'}
        </Button>
      )}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        {error instanceof Error && <Alert severity="error">{getAxiosErrorMessage(error as AxiosError)}</Alert>}
        <TextField label="Name" {...register('name')} required />
        <TextField label="Description" {...register('description')} />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            Categories
          </Typography>
          {assignedCategories.length > 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 0.5,
              }}
            >
              {assignedCategories.map((cat) => (
                <Chip
                  key={cat.id}
                  label={cat.name}
                  size="small"
                  onDelete={() => setCategoryIds((prev) => prev.filter((id) => id !== cat.id))}
                />
              ))}
            </Box>
          ) : (
            <Typography
              variant="body2"
              sx={{
                color: 'text.disabled',
                fontSize: 12,
              }}
            >
              No categories assigned
            </Typography>
          )}
          {availableCategories.length > 0 && (
            <>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  mt: 0.5,
                }}
              >
                Add categories
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 0.5,
                }}
              >
                {availableCategories.map((cat) => (
                  <Chip
                    key={cat.id}
                    label={cat.name}
                    size="small"
                    variant="outlined"
                    onClick={() => setCategoryIds((prev) => [...prev, cat.id])}
                  />
                ))}
              </Box>
            </>
          )}
        </Box>
        <TextField label="Order" type="number" {...register('order')} error={!!errors.order} />
      </Box>
    </CoreMyntModal>
  )
}
